import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Note } from '@app/models/note.model';
import { Profile, Unit } from '@app/models/profile.model';
import { User } from '@app/models/user.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { DateService } from '@app/services/date.service';
import { RecordService } from '@app/services/record.service';
import { ScheduleService } from '@app/services/schedule.service';
import { LIMIT_DATE } from '@app/shared/enum/date';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { DataSelectors } from '@app/store/data/data.selectors';
import { SelectUser } from '@app/store/data/data.classes';
import { GetInitFacesheetList } from '@app/store/record/record.classes';
import { Store } from '@ngxs/store';
import * as moment from 'moment';
import { ItemOption, addMonthDuration, durationOptions } from '../facesheet-common';
import { ComponentCanDeactivate } from '@app/can-deactivate.guard';
import { WebviewService } from '@app/services/webview.service';

@Component({
  selector: 'app-facesheet-create',
  templateUrl: './facesheet-create.component.html',
  styleUrls: ['./facesheet-create.component.scss']
})
export class FacesheetCreateComponent implements OnInit, ComponentCanDeactivate {

  record!: Note;
  profile!: Profile | null;
  dai!: User | null;
  currentUnit!: Unit | null;
  durationOptions: ItemOption[] = durationOptions;
  currentOption: string | undefined = '';
  private canLeave: boolean = false;
  @Output() formChange = new EventEmitter<FormGroup>();
  constructor(
    private dateService: DateService,
    private route: ActivatedRoute,
    public scheduleService: ScheduleService,
    private router: Router,
    public recordService: RecordService,
    private store: Store,
    private accesslog: AccesslogService,
    private cd: ChangeDetectorRef,
    private webviewService: WebviewService,
  ) {
    this.webviewService.kidsWarningBeforeChangePageIndicator(true);
  }
  private _form!: FormGroup;

  get titleName() {
    return this.record?.noteid ? '複写' : '新規作成'
  }
  get form(): FormGroup {
    return this._form;
  }
  set form(form: FormGroup) {
    this._form = form;
  }
  get caretime() {
    return this.form.get('caretime') as FormControl;
  }
  get summary() {
    return this.form.get('summary') as FormControl;
  }
  get handover() {
    return this.form.get('handover') as FormControl;
  }
  get startDate() {
    return this.form.get('startDate') as FormGroup;
  }
  get endDate() {
    return this.form.get('endDate') as FormGroup;
  }
  get subject() {
    return this.form.get('subject') as FormGroup;
  }
  set subject(value: any) {
    this.form.get('subject')?.setValue(value);
  }

  get ordName() {
    if (this.record && (this.record.odrstfnm || this.record.sndstfnm)) {
      return (this.record?.odrstfnm || '') + '　' + (this.record?.sndstfnm || '');
    }
    return this.profile?.fullName || '';
  }

  get daiName() {
    if (this.dai) {
      return this.dai.name
    }
    return '';
  }

  get unitName() {
    if (this.currentUnit) {
      return this.currentUnit.companyName;
    }
    return '';
  }

  isDecide: boolean = true;

  ngOnInit(): void {
    this.profile = this.store.selectSnapshot(AuthSelectors.profile);
    this.currentUnit = this.store.selectSnapshot(AuthSelectors.selectedUnit);
    this.dai = this.store.selectSnapshot(DataSelectors.selectedUser);
    this.route.data.subscribe((data) => {
      const { items, note } = data['record'];
      if (!items && !note) return;
      if (items && items.length > 0) {
        this.record = { ...items[0] };
        this.record.sdate = moment(this.record.sdate).startOf('date').toDate();
        this.record.edate = moment(this.record.edate).startOf('date').toDate();
      }
      if (note) {
        this.record = { ...note };
      }
      if (moment(LIMIT_DATE.EDATE).startOf('day') <= moment(this.record.edate).startOf('day')) {
        this.record.edate = this.record.sdate;
        this.isDecide = false;
      }
      this.form = this.recordService.setRecordForm(this.profile, this.record);
    });
  }
  onChange(): void {
    this.formChange.emit(this.form);
    this.currentOption = '';
  }

  startDateChange() {
    if (this.isDecide) return;
    let date = addMonthDuration(new Date(), 0);
    let endDate = moment(date);
    this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
  }

  canDeactivate() {
    return this.canLeave;
  }

  onSubjectChange(event: any): void {
    this.subject = event.target.value;
  }

  /**
   * change edate of noteData
   */
  changeDecide(event: any) {
    this.isDecide = !event.checked;
    this.startDateChange();
    if (!this.isDecide) {
      this.currentOption = '';
    } else {
      let date = addMonthDuration(new Date(), 0);
      let endDate = moment(date);
      this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
    }
    this.cd.detectChanges();
  }

  changeEndDate(value: string | number | any) {
    if (!value) {
      return;
    }
    this.currentOption = value;
    this.changeDecide({ checked: false });
    if (!this.isDecide) return;
    const startDate = this.dateService.parseFormToDate(this.startDate);
    let duration = parseInt(value) || 0;
    if (duration >= 0 && startDate) {
      let date = addMonthDuration(startDate, parseInt(value));
      let endDate = moment(date).add(-1, 'second');
      this.endDate.patchValue({ date: endDate.format('YYYY-MM-DDTHH:mm:ss'), time: endDate.format('HH:mm') });
    }
  }

  compareDate(): void {
    const startDate = this.dateService.parseFormToDate(this.startDate);
    const endDate = this.dateService.parseFormToDate(this.endDate);

    if (!startDate || !endDate) return;

    if (startDate.getTime() > endDate.getTime()) {
      this.endDate.setErrors({ compareDate: true });
    }
  }

  get isValid() {
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    const staffList = this.store.selectSnapshot(DataSelectors.staffList(objectcd));
    const getStaffName = this.scheduleService.getStaffName(staffList);
    const formValue = this.scheduleService.getFormValue(this.form.value, getStaffName(this.record));
    let sdate = moment(formValue.sdate).toDate();
    let edate = moment(formValue.edate).toDate();
    return sdate <= edate;
  }

  next() {
    // create a snapshot of new facesheet here
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    const staffList = this.store.selectSnapshot(DataSelectors.staffList(objectcd));
    const getStaffName = this.scheduleService.getStaffName(staffList);
    const formValue = this.scheduleService.getFormValue(this.form.value, getStaffName(this.record));
    this.record.sdate = moment(formValue.sdate).toDate();
    this.record.edate = moment(formValue.edate).toDate();
    this.record.lifeflg = formValue.lifeflg;
    this.record.pickupflg = formValue.pickupflg;
    this.record.subject = formValue.subject;
    this.store.dispatch(new GetInitFacesheetList({ note: { ...this.record, isDecide: this.isDecide } }));
    const { objkbn, objcd } = this.record;
    const path = 'facesheet-edit';
    this.canLeave = true;
    const routerlink = ['',
      {
        outlets: {
          edit: [path, { noteid: null, objkbn, objcd }],
        },
      },
    ];
    this.router.navigate(routerlink, { skipLocationChange: true, queryParamsHandling: "preserve" });
  }

  cancel() {
    if (this.dai) {
      this.store.dispatch(new SelectUser(this.dai));
    }
    this.close();
  }

  protected close() {
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
    this.router.navigate(['', { outlets: { edit: null } }],{
      replaceUrl: true, queryParamsHandling: "preserve"
    }).finally(() => {
      history.go(-1);
    });
  }
}